import React, {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import {
  $convertToMarkdownString,
  $convertFromMarkdownString,
} from "@lexical/markdown";
import {
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $getRoot,
  FORMAT_TEXT_COMMAND,
  COMMAND_PRIORITY_LOW,
  FOCUS_COMMAND,
  BLUR_COMMAND,
  LexicalEditor,
} from "lexical";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  ListNode,
} from "@lexical/list";
import { $createHeadingNode, $isHeadingNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";

import { createEditorConfig } from "../LexicalEditor/config";
import { CUSTOM_TRANSFORMERS } from "../LexicalEditor/transformers";
import { htmlToMarkdown } from "@/shared/utils/parsers/htmlToMarkdown";
import { cn } from "@/shared/utils/shadcn";
import { Row } from "@/shared/ui/Blocks";
import { IconButton } from "@/shared/ui/IconButton";
import { IconName } from "@/shared/ui/Icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import { CustomTagsData } from "@/shared/widgets/MarkdownText/_components/MarkdownComponents";

type MarkFormat = "bold" | "italic" | "underline" | "code" | "strikethrough";
type BlockFormat =
  | "heading-one"
  | "heading-two"
  | "bulleted-list"
  | "numbered-list"
  | "paragraph";

interface ToolbarButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  icon: React.ReactNode;
}

function ToolbarButton({ active, icon, ...props }: ToolbarButtonProps) {
  return (
    <IconButton
      variant="ghost"
      iconProps={{ size: "sm" }}
      icon={icon as IconName}
      color="black"
      type="button"
      className={cn("px-2 rounded-xl", active && "bg-grey-300")}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      {...props}
    />
  );
}

function Toolbar({
  showWisGptCopy,
  className,
}: {
  showWisGptCopy?: boolean;
  className?: string;
}) {
  const [editor] = useLexicalComposerContext();
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    "heading-one": false,
    "heading-two": false,
    "bulleted-list": false,
    "numbered-list": false,
  });

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        let blockType = "paragraph";
        const anchorNode = selection.anchor.getNode();
        const element =
          anchorNode.getKey() === "root"
            ? anchorNode
            : anchorNode.getTopLevelElementOrThrow();
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);

        if (parentList) {
          blockType =
            parentList.getListType() === "bullet"
              ? "bulleted-list"
              : "numbered-list";
        } else if ($isHeadingNode(element)) {
          const tag = element.getTag();
          blockType =
            tag === "h1"
              ? "heading-one"
              : tag === "h2"
              ? "heading-two"
              : "paragraph";
        }

        setActiveFormats({
          bold: selection.hasFormat("bold"),
          italic: selection.hasFormat("italic"),
          underline: selection.hasFormat("underline"),
          "heading-one": blockType === "heading-one",
          "heading-two": blockType === "heading-two",
          "bulleted-list": blockType === "bulleted-list",
          "numbered-list": blockType === "numbered-list",
        });
      });
    });
  }, [editor]);

  const handleMarkToggle = (format: "bold" | "italic" | "underline") => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const handleBlockToggle = (format: BlockFormat) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (activeFormats["bulleted-list"] || activeFormats["numbered-list"]) {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        if (format === "bulleted-list" || format === "numbered-list") {
          if (
            format !==
            (activeFormats["bulleted-list"] ? "bulleted-list" : "numbered-list")
          ) {
            if (format === "bulleted-list") {
              editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            }
            if (format === "numbered-list") {
              editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            }
          }
          return;
        }
      }

      switch (format) {
        case "heading-one":
          $setBlocksType(selection, () => $createHeadingNode("h1"));
          break;
        case "heading-two":
          $setBlocksType(selection, () => $createHeadingNode("h2"));
          break;
        case "bulleted-list":
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          break;
        case "numbered-list":
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          break;
        case "paragraph":
          $setBlocksType(selection, () => $createParagraphNode());
          break;
      }
    });
  };

  const handleCopyFromWisGPT = () => {
    const wisGptMessages = document.querySelectorAll(
      ".wisgpt-message-assistant"
    );
    if (wisGptMessages.length === 0) return;
    const lastMessage = wisGptMessages[wisGptMessages.length - 1];
    const messageContent = lastMessage.getAttribute("data-message");
    if (!messageContent) return;
    editor.update(() => {
      $convertFromMarkdownString(messageContent, CUSTOM_TRANSFORMERS);
    });
  };

  return (
    <div className={cn("flex items-center justify-between p-2", className)}>
      <Row className="gap-x-1">
        <ToolbarButton
          active={activeFormats.bold}
          icon="Bold"
          onClick={() => handleMarkToggle("bold")}
        />
        <ToolbarButton
          active={activeFormats.italic}
          icon="Italic"
          onClick={() => handleMarkToggle("italic")}
        />
        <ToolbarButton
          active={activeFormats.underline}
          icon="Underline"
          onClick={() => handleMarkToggle("underline")}
        />
        <ToolbarButton
          active={activeFormats["heading-one"]}
          icon="H1"
          onClick={() => handleBlockToggle("heading-one")}
        />
        <ToolbarButton
          active={activeFormats["heading-two"]}
          icon="H2"
          onClick={() => handleBlockToggle("heading-two")}
        />
        <ToolbarButton
          active={activeFormats["bulleted-list"]}
          icon="BulletList"
          onClick={() => handleBlockToggle("bulleted-list")}
        />
        <ToolbarButton
          active={activeFormats["numbered-list"]}
          icon="NumberList"
          onClick={() => handleBlockToggle("numbered-list")}
        />
      </Row>
      {showWisGptCopy && (
        <IconButton
          size="sm"
          icon="SetAsNote"
          label="Copy from WisGPT"
          className="bg-purple-500 hover:bg-purple-500/60 mr-2"
          onClick={handleCopyFromWisGPT}
        />
      )}
    </div>
  );
}

// Character counter component
function CharacterCounter({
  current,
  limit,
}: {
  current: number;
  limit: number;
}) {
  const isOverLimit = current > limit;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "absolute z-50 bottom-0 right-4 text-xs px-2 py-1 rounded bg-white cursor-default",
            isOverLimit ? "text-orange-500" : "text-gray-500 "
          )}
        >
          {current}/{limit}
        </div>
      </TooltipTrigger>
      <TooltipContent side="left" align="center">
        Recommended character limit.
      </TooltipContent>
    </Tooltip>
  );
}

// Custom inline tokens ([DIMENSION:id], [DIMENSION:id:label], [PERSON:id:name],
// [FILE:id:name], reference tags) must reach the markdown parser unescaped so
// the matching transformers can turn them into nodes. htmlToMarkdown (Turndown)
// escapes the surrounding brackets to "\[...\]"; left escaped, they fail the
// import transformers and get double-escaped on the next export (visible
// backslashes in the rendered summary). Restore the brackets for these known
// tokens only, so unrelated escaped brackets in prose stay escaped.
const CUSTOM_TAG_TOKEN_REGEX =
  /\\?\[((?:DIMENSION_HIGHLIGHT|DIMENSION|PERSON|FILE):\d+(?::[^\]\\]*)?|ASSESSMENT_REPORT|ASSESSMENT_PSYCH|PROJECT_DESCRIPTION)\\?\]/g;

const normalizeMarkdownForEditor = (content?: string): string =>
  htmlToMarkdown(content || "").replace(CUSTOM_TAG_TOKEN_REGEX, "[$1]");

// Sync plugins (MarkdownSync, FocusTracking)
function MarkdownSyncPlugin({
  initialMarkdown,
  onChange,
  onBlur,
}: {
  initialMarkdown?: string;
  onChange?: (m: string) => void;
  onBlur?: (m: string) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const [isInitialized, setIsInitialized] = useState(false);
  const markdownRef = useRef("");

  useEffect(() => {
    if (!isInitialized) {
      // Content created by the previous (Slate) editor is stored as HTML, while
      // Lexical only understands markdown. Normalize any HTML to markdown first
      // so legacy summaries/notes load instead of opening as an empty editor,
      // and keep custom tag tokens unescaped so they round-trip cleanly. This
      // mirrors the read-only MarkdownText display path. For content that is
      // already markdown, this is a no-op.
      const normalizedMarkdown = normalizeMarkdownForEditor(initialMarkdown);
      if (normalizedMarkdown) {
        editor.update(() => {
          $convertFromMarkdownString(normalizedMarkdown, CUSTOM_TRANSFORMERS);
        });
        editor.getEditorState().read(() => {
          markdownRef.current = $convertToMarkdownString(CUSTOM_TRANSFORMERS);
        });
      }
      setIsInitialized(true);
    }
  }, [editor, initialMarkdown, isInitialized]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const markdown = $convertToMarkdownString(CUSTOM_TRANSFORMERS);
        if (markdown !== markdownRef.current) {
          markdownRef.current = markdown;
          onChange?.(markdown);
        }
      });
    });
  }, [editor, onChange]);

  useEffect(() => {
    return editor.registerCommand(
      BLUR_COMMAND,
      () => {
        onBlur?.(markdownRef.current);
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onBlur]);

  return null;
}

export interface RichTextEditorProps {
  value?: string;
  defaultValue: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  editorClassName?: string;
  toolbarClassName?: string;
  ToolbarRightComponent?: React.ReactNode;
  showWisGptCopy?: boolean;
  limit?: number;
  alwaysShowToolbar?: boolean;
  tagsData?: CustomTagsData;
}

export interface RichTextEditorRef {
  focus: () => void;
}

function countTextLength(editor: LexicalEditor): number {
  let count = 0;
  editor.getEditorState().read(() => {
    const root = $getRoot();
    count = root.getTextContent().length;
  });
  return count;
}

function FocusTrackingPlugin({
  onFocusChange,
}: {
  onFocusChange: (f: boolean) => void;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        FOCUS_COMMAND,
        () => {
          onFocusChange(true);
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        BLUR_COMMAND,
        () => {
          onFocusChange(false);
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, onFocusChange]);
  return null;
}

function CharacterCountPlugin({ onChange }: { onChange: (c: number) => void }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerUpdateListener(() => {
      onChange(countTextLength(editor));
    });
  }, [editor, onChange]);
  return null;
}

function EditorRefPlugin({
  onEditorReady,
}: {
  onEditorReady: (ed: LexicalEditor) => void;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    onEditorReady(editor);
  }, [editor, onEditorReady]);
  return null;
}

export const RichTextEditor = forwardRef<
  RichTextEditorRef,
  RichTextEditorProps
>(
  (
    {
      defaultValue,
      onChange,
      onBlur,
      placeholder = "Type something...",
      editorClassName,
      toolbarClassName,
      ToolbarRightComponent,
      showWisGptCopy = false,
      limit,
      alwaysShowToolbar = false,
      tagsData,
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const [characterCount, setCharacterCount] = useState(0);
    const editorRef = useRef<LexicalEditor | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          editorRef.current?.focus();
        },
      }),
      []
    );

    const initialConfig = useMemo(
      () => createEditorConfig(`lexical-rte-${Date.now()}`),
      []
    );

    return (
      <div className="rounded-lg relative">
        <LexicalComposer initialConfig={initialConfig}>
          <Row
            className={cn(
              "transition-all duration-300 overflow-hidden justify-between items-center bg-grey-100 w-full rounded-t-lg",
              focused || alwaysShowToolbar
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-2"
            )}
            style={{
              maxHeight: focused || alwaysShowToolbar ? 56 : 0,
              transition:
                "max-height 0.3s ease, opacity 0.3s ease, transform 0.3s ease",
            }}
          >
            <Toolbar
              className={cn("w-full", toolbarClassName)}
              showWisGptCopy={showWisGptCopy}
            />
            {ToolbarRightComponent && (
              <div className="p-2">{ToolbarRightComponent}</div>
            )}
          </Row>

          <div className="relative border-x border-b border-transparent rounded-b-lg overflow-hidden">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={cn(
                    "!p-4 !min-h-[200px] focus:outline-none !max-h-[300px] overflow-y-auto w-full",
                    editorClassName,
                    defaultValue && "!pb-2",
                    (focused || alwaysShowToolbar) && "!pb-4"
                  )}
                />
              }
              placeholder={
                <div className="absolute top-4 left-4 text-sm mt-4 text-gray-900 pointer-events-none opacity-50">
                  {placeholder}
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>

          <HistoryPlugin />
          <ListPlugin />
          <MarkdownShortcutPlugin transformers={CUSTOM_TRANSFORMERS} />
          <MarkdownSyncPlugin
            initialMarkdown={defaultValue}
            onChange={onChange}
            onBlur={onBlur}
          />

          <FocusTrackingPlugin onFocusChange={setFocused} />
          <CharacterCountPlugin onChange={setCharacterCount} />
          <EditorRefPlugin onEditorReady={(ed) => (editorRef.current = ed)} />

          {limit && limit > 0 && (
            <div
              className={cn(
                "transition-all duration-300 absolute bottom-4 right-4 z-50",
                focused || alwaysShowToolbar
                  ? "opacity-100 translate-y-0 pointer-events-auto"
                  : "opacity-0 translate-y-2 pointer-events-none"
              )}
            >
              <CharacterCounter current={characterCount} limit={limit} />
            </div>
          )}
        </LexicalComposer>
      </div>
    );
  }
);

RichTextEditor.displayName = "RichTextEditor";
